import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Check, Heart, Star, MessageCircle, Eye, Zap, ArrowLeft, Loader2, Sparkles, Gift, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const Premium = () => {
  const [loading, setLoading] = useState(null);
  const [trialLoading, setTrialLoading] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [showTrialHighlight, setShowTrialHighlight] = useState(false);
  const [premiumDisabled, setPremiumDisabled] = useState(false);
  const { user, token, refreshUser, premiumEnabled } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const fetchTrialStatus = async () => {
    try {
      const res = await axios.get(`${API}/trial/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialStatus(res.data);
      setPremiumDisabled(res.data.premium_disabled || false);
    } catch (err) {
      console.error('Kunne ikke hente trial status');
    }
  };

  useEffect(() => {
    fetchTrialStatus();
    // Check if user came from "try free" button
    if (searchParams.get('trial') === 'true') {
      setShowTrialHighlight(true);
      // Scroll to trial section after a short delay
      setTimeout(() => {
        const trialSection = document.getElementById('trial-section');
        if (trialSection) {
          trialSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, [searchParams]);

  // Show "coming soon" if premium is disabled
  if (premiumDisabled || premiumEnabled === false) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <div className="max-w-lg mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold">Flirra Premium</h1>
          </div>

          {/* Coming Soon */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-slate-500/10 to-slate-400/10 rounded-2xl border border-slate-500/30 p-8 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-slate-500/20 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-slate-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Kommer Snart!</h2>
            <p className="text-muted-foreground mb-6">
              Premium-funktioner er midlertidigt deaktiveret mens vi bygger vores community op. 
              Alle basis-funktioner er gratis at bruge!
            </p>
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-green-400 mb-2">Gratis lige nu:</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>✅ Ubegrænsede swipes</li>
                <li>✅ Matching og chat</li>
                <li>✅ Profil-oprettelse</li>
                <li>✅ Filtrering</li>
              </ul>
            </div>
            <Button
              onClick={() => navigate('/discover')}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              Start med at swipe
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  const handleStartTrial = async (packageId) => {
    setTrialLoading(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/trial/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke starte prøveperiode');
      setTrialLoading(null);
    }
  };

  const plans = [
    {
      id: 'basic',
      name: 'Basis',
      price: 79,
      color: 'from-secondary to-secondary/50',
      features: [
        { icon: <Heart className="w-4 h-4" />, text: 'Ubegrænsede likes' },
        { icon: <Eye className="w-4 h-4" />, text: 'Se hvem der har liket dig' },
        { icon: <MessageCircle className="w-4 h-4" />, text: 'Basis filtre' }
      ]
    },
    {
      id: 'gold',
      name: 'Guld',
      price: 149,
      popular: true,
      color: 'from-primary to-primary/70',
      features: [
        { icon: <Heart className="w-4 h-4" />, text: 'Alt i Basis' },
        { icon: <Star className="w-4 h-4" />, text: '5 Super Likes om dagen' },
        { icon: <Zap className="w-4 h-4" />, text: 'Prioriterede matches' },
        { icon: <Sparkles className="w-4 h-4" />, text: 'AI kompatibilitetsscore' }
      ]
    },
    {
      id: 'platinum',
      name: 'Platin',
      price: 229,
      color: 'from-accent to-accent/70',
      features: [
        { icon: <Crown className="w-4 h-4" />, text: 'Alt i Guld' },
        { icon: <Sparkles className="w-4 h-4" />, text: 'AI matchmaking' },
        { icon: <Eye className="w-4 h-4" />, text: 'Læsekvitteringer' },
        { icon: <Star className="w-4 h-4" />, text: 'Ubegrænsede Super Likes' },
        { icon: <Zap className="w-4 h-4" />, text: 'Profil boost' }
      ]
    }
  ];

  const handleSubscribe = async (packageId) => {
    setLoading(packageId);
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke starte checkout');
      setLoading(null);
    }
  };

  const isOnTrial = user?.premium_via_trial || trialStatus?.trial_info?.trial_active;
  const trialDaysRemaining = trialStatus?.trial_info?.days_remaining || 0;
  const canStartTrial = trialStatus?.can_start_trial;

  if (user?.is_premium && !isOnTrial) {
    return (
      <div className="min-h-screen bg-background">
        <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <span className="text-lg font-display font-semibold text-foreground">Premium</span>
          </div>
        </div>

        <div className="pt-20 px-4 pb-8">
          <div className="max-w-lg mx-auto text-center py-12">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Crown className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-2xl font-display font-bold text-foreground">Du er Premium-medlem!</h2>
            <p className="text-muted-foreground mt-2 mb-6">
              Tak fordi du er {user.premium_tier === 'basic' ? 'Basis' : user.premium_tier === 'gold' ? 'Guld' : 'Platin'} medlem.
            </p>
            <Badge className="bg-primary text-primary-foreground text-lg px-4 py-2">
              {user.premium_tier === 'basic' ? 'Basis' : user.premium_tier === 'gold' ? 'Guld' : 'Platin'} Plan
            </Badge>
            <div className="mt-8">
              <Link to="/discover">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  Start med at opdage
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-lg font-display font-semibold text-foreground">Bliv Premium</span>
        </div>
      </div>

      <div className="pt-20 px-4 pb-8">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-8"
          >
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-display font-bold text-foreground">
              Lås Op For Din Kærlighedshistorie
            </h1>
            <p className="text-muted-foreground mt-2 max-w-sm mx-auto">
              Få flere matches, se hvem der kan lide dig, og find din perfekte partner hurtigere.
            </p>
          </motion.div>

          {canStartTrial && (
            <motion.div
              id="trial-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`rounded-2xl p-4 mb-6 border transition-all duration-500 ${
                showTrialHighlight 
                  ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/50 ring-2 ring-green-500/30' 
                  : 'bg-gradient-to-r from-accent/20 to-primary/20 border-accent/30'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  showTrialHighlight ? 'bg-green-500/20' : 'bg-accent/20'
                }`}>
                  <Gift className={`w-6 h-6 ${showTrialHighlight ? 'text-green-500' : 'text-accent'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">3 dages gratis prøveperiode!</h3>
                  <p className="text-sm text-muted-foreground">
                    Prøv alle premium-funktioner gratis i 3 dage. Annuller når som helst.
                  </p>
                </div>
              </div>
              <Button
                onClick={() => handleStartTrial('gold')}
                disabled={trialLoading !== null}
                className={`w-full mt-4 rounded-full ${
                  showTrialHighlight
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                    : 'bg-gradient-to-r from-accent to-primary hover:from-accent/90 hover:to-primary/90'
                }`}
                data-testid="start-trial-btn"
              >
                {trialLoading === 'gold' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Start gratis prøveperiode
              </Button>
            </motion.div>
          )}

          {isOnTrial && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-accent/20 to-primary/20 rounded-2xl p-4 mb-6 border border-accent/30"
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Din prøveperiode er aktiv!</h3>
                  <p className="text-sm text-muted-foreground">
                    {trialDaysRemaining} dag{trialDaysRemaining !== 1 ? 'e' : ''} tilbage. Dit abonnement fornyes automatisk.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          <div className="space-y-4">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-card rounded-2xl border ${
                  plan.popular ? 'border-primary shadow-lg' : 'border-border'
                } overflow-hidden`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-bl-lg">
                    Mest Populær
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-display font-bold text-foreground">{plan.name}</h3>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-3xl font-display font-bold text-foreground">{plan.price} kr</span>
                        <span className="text-muted-foreground">/måned</span>
                      </div>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center`}>
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <div className="text-primary">{feature.icon}</div>
                        <span className="text-foreground">{feature.text}</span>
                      </div>
                    ))}
                  </div>

                  {canStartTrial && (
                    <Button
                      onClick={() => handleStartTrial(plan.id)}
                      disabled={trialLoading !== null}
                      variant="outline"
                      className="w-full rounded-full h-12 mb-3 border-accent text-accent hover:bg-accent/10"
                      data-testid={`trial-${plan.id}-btn`}
                    >
                      {trialLoading === plan.id ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Gift className="w-4 h-4 mr-2" />
                          Start 3 dages gratis prøve
                        </>
                      )}
                    </Button>
                  )}

                  <Button
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading !== null}
                    className={`w-full rounded-full h-12 ${
                      plan.popular ? 'bg-primary hover:bg-primary/90' : ''
                    }`}
                    variant={plan.popular ? 'default' : 'outline'}
                    data-testid={`subscribe-${plan.id}-btn`}
                  >
                    {loading === plan.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      `Få ${plan.name}`
                    )}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 bg-muted/50 rounded-2xl p-6"
          >
            <h3 className="font-display font-semibold text-foreground mb-4">Hvorfor blive Premium?</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">3x flere matches</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">Ingen daglige grænser</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">AI-drevet indsigt</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">Prioriteret support</span>
              </div>
            </div>
          </motion.div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Annuller når som helst. Sikker betaling via Stripe.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Premium;
